'use client';

import { Typography } from '@mui/material';
import type { Workspace } from '@repo/api-gw-sdk';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from 'react';

import { Loader } from '@/components/layout/loading';
import { useDAL } from '@/contexts/useDAL';

interface WorkspaceContextType {
  workspaceId: string | null;
  workspaceName: string | null;
  workspaceCloud: string | null;
  workspaceRegion: string | null;
  loading: boolean;
  workspaces: Workspace[];
  setActiveWorkspace: (id: string) => void;
}

const WorkspaceContext = createContext<WorkspaceContextType>({
  workspaceId: null,
  workspaceName: null,
  workspaceCloud: null,
  workspaceRegion: null,
  loading: true,
  workspaces: [],
  setActiveWorkspace: () => {},
});

export function WorkspaceProvider({ children }: { children: ReactNode }) {
  const dal = useDAL();
  const [workspaceId, setWorkspaceId] = useState<string | null>(null);
  const [workspaceName, setWorkspaceName] = useState<string | null>(null);
  const [workspaceCloud, setWorkspaceCloud] = useState<string | null>(null);
  const [workspaceRegion, setWorkspaceRegion] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [workspaces, setWorkspaces] = useState<Workspace[]>([]);

  const setActiveWorkspace = useCallback(
    (id: string) => {
      const ws = workspaces.find((w) => w.id === id);
      if (ws) {
        setWorkspaceId(ws.id);
        setWorkspaceName(ws.name);
        setWorkspaceCloud(ws.cloud);
        setWorkspaceRegion(ws.region);
      }
    },
    [workspaces]
  );

  useEffect(() => {
    dal.lakehouse
      .listWorkspaces()
      .then((result) => {
        setWorkspaces(result);
        if (result.length > 0) {
          const ws = result[0];
          setWorkspaceId(ws.id);
          setWorkspaceName(ws.name);
          setWorkspaceCloud(ws.cloud);
          setWorkspaceRegion(ws.region);
        }
      })
      .catch(console.error)
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <WorkspaceContext.Provider
      value={{
        workspaceId,
        workspaceName,
        workspaceCloud,
        workspaceRegion,
        loading,
        workspaces,
        setActiveWorkspace,
      }}
    >
      {children}
    </WorkspaceContext.Provider>
  );
}

export function useWorkspace() {
  return useContext(WorkspaceContext);
}

export function WorkspaceGuard({
  children,
}: {
  children: (workspaceId: string) => ReactNode;
}) {
  const { workspaceId, loading } = useWorkspace();
  if (loading) return <Loader />;
  if (!workspaceId)
    return (
      <Typography color='text.secondary'>No workspace available</Typography>
    );
  return <>{children(workspaceId)}</>;
}
